/**
 * The Supabase auth session, followed for the life of the app.
 *
 * This is the first thing the route tree waits on: `loading` is true until
 * the persisted session has been looked up, and until then no route can
 * decide whether it is looking at somebody. Who that somebody is on ONDO is
 * `useMe`'s question, asked once a user id exists.
 */

import { useEffect, useState } from "react";

import type { Session } from "@ondo/supabase/browser";

import { supabase } from "./supabase.ts";

export interface SessionState {
  session: Session | null;
  /** True until the initial session lookup resolves. */
  loading: boolean;
}

/**
 * Tracks the Supabase auth session.
 *
 * Reads the persisted session on mount, then follows auth state changes
 * (sign-in, sign-out, token refresh) for the life of the component.
 */
export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}
